import { useTranslation } from "react-i18next";
import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import { memo, useCallback } from "react";
import { Grid } from "@mui/material";
import { allowedActionSchema, TAllowedActionForm } from "./allowedActionSchema";
import {
  createTicketAutomationReceivedRuleAllowedAction,
  getTicketAutomationReceivedRuleAllowedActionById,
  updateTicketAutomationReceivedRuleAllowedActionById,
} from "../../../../../../../services/ticketAutomationReceivedRuleAllowedAction.service";
import { IAllowedActionProps } from "./models";
import { AllowedActionFields } from "./AllowedActionFields";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutomationReceivedRuleAllowedAction.";

export const AllowedAction = memo(
  ({
    id,
    ticketAutomationReceivedRuleId,
    onBackButton,
  }: IAllowedActionProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createTicketAutomationReceivedRuleAllowedAction,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateTicketAutomationReceivedRuleAllowedActionById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getTicketAutomationReceivedRuleAllowedActionById,
    );

    const updateHandler = (v: TAllowedActionForm, id: string) =>
      update({
        item: {
          id: id,
          ...v,
          ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
          consultationTicketAutomationReceivedRuleId:
            v.consultationTicketAutomationReceivedRule?.id,
          examTicketAutomationReceivedRuleId:
            v.examTicketAutomationReceivedRule?.id,
          featuredTicketAutomationReceivedRuleId:
            v.featuredTicketAutomationReceivedRule?.id,
          returnTicketAutomationReceivedRuleId:
            v.returnTicketAutomationReceivedRule?.id,
        },
        id: id,
      });

    const saveHandler = (v: TAllowedActionForm) =>
      save({
        ...v,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
        consultationTicketAutomationReceivedRuleId:
          v.consultationTicketAutomationReceivedRule?.id,
        examTicketAutomationReceivedRuleId:
          v.examTicketAutomationReceivedRule?.id,
        featuredTicketAutomationReceivedRuleId:
          v.featuredTicketAutomationReceivedRule?.id,
        returnTicketAutomationReceivedRuleId:
          v.returnTicketAutomationReceivedRule?.id,
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TAllowedActionForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TAllowedActionForm = {
            ...data,
            checkIn: data.checkIn ?? true,
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TAllowedActionForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "allowed_action")}
        schema={allowedActionSchema}
        subtitle={t(translationPath + "new_allowed_action")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <AllowedActionFields />
          </Grid>
        )}
      />
    );
  },
);
